import { type FC, useRef } from "react";
import { Field, Form } from "react-final-form";
import Grid from "@mui/material/Grid";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { DateTimePicker, Switch } from "@/shared/components/Form";
import { ShareUrl } from "@/shared/components/ShareUrl";
import { useIsMobile } from "@/shared/hooks";
import { useCollectionTypesQuery } from "@/partner/entities/contents";
import { CollectionContentSortType } from "@/types/graphql/partner.generated";
import { PreviewImageField } from "@/partner/features/videos/ui/PreviewImageField";
import { TagsByTypeField } from "@/partner/features/videos/ui/TagsByTypeField";
import { CountryAccessField } from "@/partner/features/videos/ui/CountryAccessField";
import { ProductsField } from "@/partner/features/products";
import type { CollectionFormValues } from "../model";
import { CollectionsField } from "./CollectionsField";
import { ChildrenContents } from "./ChildrenContents";

const FORM_ID = "collection-form";
// Совпадает со SPACING/FORM_SPACING референсного CollectionForm.js
const SPACING = 1;
const FORM_SPACING = 2;

// Селект режима сортировки содержимого (референс показывает три режима;
// UPDATED в enum есть, но в форме не предлагается — как в референсе)
const SORT_OPTIONS: { value: CollectionContentSortType; labelKey: string }[] = [
  {
    value: CollectionContentSortType.LatestType,
    labelKey: "collections.form.sortLatest",
  },
  {
    value: CollectionContentSortType.OldestType,
    labelKey: "collections.form.sortOldest",
  },
  {
    value: CollectionContentSortType.ManualType,
    labelKey: "collections.form.sortManual",
  },
];

interface CollectionFormProps {
  title: string;
  loading?: boolean;
  initialValues?: Partial<CollectionFormValues>;
  onSubmit: (values: CollectionFormValues) => Promise<void>;
  onClose: () => void;
  /** id коллекции (edit) — включает панель состава справа */
  collectionId?: string | null;
  /** Публичный URL коллекции (edit) */
  url?: string | null;
}

// Порт референсного CollectionForm.js на наш стек (react-final-form, как
// ContentForm). Компоновка референса: колонки md:7/md:5 (складываются на
// md-даун, без column-reverse), слева ДВЕ карточки — «название+описание» и
// «настройки» (сортировка → гео-доступ → теги → тип → родительские
// коллекции → дата публикации + превью → свитчи); справа шаринг-блок
// (на мобиле он первым в левой колонке) и состав коллекции (edit) либо
// пустая карточка (create).
export const CollectionForm: FC<CollectionFormProps> = ({
  title,
  loading = false,
  initialValues = {},
  onSubmit,
  onClose,
  collectionId = null,
  url = null,
}) => {
  const { t } = useTranslation();
  // Референсный useIsMobile() без аргумента — брейкпоинт совпадает с
  // моментом складывания колонок md:7/md:5 в одну
  const isMobile = useIsMobile("md");
  const isDraft = !initialValues.publishAt;
  // true = «Опубликовать» (publishAt=now, если пусто), false = черновик/скрыть
  // (см. ContentForm — тот же паттерн двух submit-кнопок)
  const publishIntentRef = useRef(!isDraft);

  const { data: collectionTypesData } = useCollectionTypesQuery();
  const collectionTypes = collectionTypesData?.collectionTypes ?? [];

  const handleFormSubmit = async (values: CollectionFormValues) => {
    const publish = publishIntentRef.current;
    await onSubmit({
      ...values,
      publishAt: publish ? values.publishAt || new Date().toISOString() : null,
    });
  };

  return (
    <Form<CollectionFormValues>
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      subscription={{ values: true }}
      render={({ handleSubmit, values }) => {
        // Референсный shareComponent: на мобиле рендерится первым в левой
        // колонке, на десктопе — в правой над ChildrenContents
        const shareComponent = collectionId ? (
          <Card elevation={0}>
            <CardContent>
              <Stack spacing={2}>
                <Alert severity="info" variant="outlined">
                  {values?.publishAt
                    ? t("collections.form.availableInfoAlert")
                    : t("collections.form.notYetAvailableInfoAlert")}
                </Alert>
                <ShareUrl
                  url={url}
                  label={t("platformCustomCovers.form.link")}
                  copiedMessage={t("products.form.shareUrlCopied")}
                />
              </Stack>
            </CardContent>
          </Card>
        ) : null;

        return (
          <form id={FORM_ID} onSubmit={handleSubmit}>
            <AppBar color="default" position="fixed" elevation={0}>
              <Toolbar>
                <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                  {title}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip
                    title={
                      isDraft
                        ? t("collections.form.publishTooltip")
                        : t("videos.form.saveChangesTooltip")
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        type="submit"
                        form={FORM_ID}
                        disabled={loading}
                        onClick={() => {
                          publishIntentRef.current = true;
                        }}
                      >
                        {isDraft
                          ? t("common.actions.publish")
                          : t("common.actions.save")}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isDraft
                        ? t("videos.form.saveDraftTooltip")
                        : t("videos.form.hideTooltip")
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        type="submit"
                        form={FORM_ID}
                        disabled={loading}
                        onClick={() => {
                          publishIntentRef.current = false;
                        }}
                      >
                        {isDraft
                          ? t("videos.form.saveDraftButton")
                          : t("common.actions.hide")}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Toolbar>
              <Divider />
            </AppBar>

            <Box sx={{ p: 1, mt: { xs: "56px", sm: "64px" } }}>
              <Grid container spacing={SPACING}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Grid container spacing={SPACING}>
                    {isMobile && shareComponent && (
                      <Grid size={12}>{shareComponent}</Grid>
                    )}

                    <Grid size={12}>
                      <Card elevation={0}>
                        <CardContent sx={{ pt: 3 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={12}>
                              <Field name="title">
                                {({ input, meta }) => (
                                  <TextField
                                    {...input}
                                    autoFocus
                                    required
                                    label={t("videos.form.titleLabel")}
                                    fullWidth
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                  />
                                )}
                              </Field>
                            </Grid>
                            <Grid size={12}>
                              <Field name="description">
                                {({ input }) => (
                                  <TextField
                                    {...input}
                                    label={t("videos.form.descriptionLabel")}
                                    multiline
                                    fullWidth
                                    variant="outlined"
                                  />
                                )}
                              </Field>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={12}>
                      <Card elevation={0}>
                        <CardContent sx={{ pt: 3 }}>
                          <Grid container spacing={FORM_SPACING}>
                            <Grid size={12}>
                              <Field name="collectionContentSort">
                                {({ input }) => (
                                  <TextField
                                    select
                                    required
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    label={t("collections.form.sortLabel")}
                                    value={
                                      input.value ||
                                      CollectionContentSortType.LatestType
                                    }
                                    onChange={input.onChange}
                                  >
                                    {SORT_OPTIONS.map(({ value, labelKey }) => (
                                      <MenuItem key={value} value={value}>
                                        {t(labelKey)}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              </Field>
                            </Grid>

                            <Grid size={12}>
                              <CountryAccessField />
                            </Grid>

                            <Grid size={12}>
                              <TagsByTypeField />
                            </Grid>

                            {collectionTypes.length > 0 && (
                              <Grid size={12}>
                                <Field name="collectionTypeId">
                                  {({ input }) => (
                                    <TextField
                                      select
                                      fullWidth
                                      variant="outlined"
                                      label={t(
                                        "collections.form.collectionTypeLabel"
                                      )}
                                      value={input.value || ""}
                                      onChange={(event) =>
                                        input.onChange(
                                          event.target.value || null
                                        )
                                      }
                                    >
                                      <MenuItem value="">
                                        {t(
                                          "collections.form.collectionTypeNone"
                                        )}
                                      </MenuItem>
                                      {collectionTypes.map((collectionType) => (
                                        <MenuItem
                                          key={collectionType.id}
                                          value={collectionType.id}
                                        >
                                          {collectionType.title}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                </Field>
                              </Grid>
                            )}

                            <Grid size={12}>
                              <ProductsField />
                            </Grid>

                            <Grid size={12}>
                              <CollectionsField excludeId={collectionId} />
                            </Grid>

                            <Grid size={{ xs: 12, lg: 6 }}>
                              <DateTimePicker
                                name="publishAt"
                                label={t("videos.form.publishAtLabel")}
                                outputFormat="iso"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, lg: 6 }}>
                              <PreviewImageField />
                            </Grid>

                            <Grid size={12}>
                              <FormControl component="fieldset">
                                <FormGroup>
                                  <Switch
                                    name="hideChildrenContentFromLists"
                                    label={t(
                                      "collections.form.hideChildrenContentFromListsLabel"
                                    )}
                                  />
                                  <Switch
                                    name="hideInGeneralLists"
                                    label={t(
                                      "videos.form.hideInGeneralListsLabel"
                                    )}
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {collectionId ? (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Stack spacing={1}>
                      {!isMobile && shareComponent}
                      <ChildrenContents
                        collectionId={collectionId}
                        sort={
                          values?.collectionContentSort ||
                          CollectionContentSortType.LatestType
                        }
                      />
                    </Stack>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Card
                      elevation={0}
                      sx={{ minHeight: 200, width: "100%" }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </form>
        );
      }}
    />
  );
};
